import React from 'react';
import { cn } from '@hts/ui';
import { useGet, apiPost } from '../../hooks/useApi.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';

interface RuntimeSignalDto {
  method: string;
  outcome: 'verified' | 'failed' | 'unavailable' | 'pending';
  reason: string;
  confidence: number;
}

interface VerificationDto {
  callId: string;
  proposedIdentity: { contactId?: number; contactName?: string | null; companyId?: number; companyName?: string | null; ticketId?: number | null } | null;
  proposalReason: string;
  signals: RuntimeSignalDto[];
  decision: { risk: string; disposition: string; permitted: boolean; requiredMethods: string[]; reasons: string[]; assurance: number };
  session: { verified: boolean; verifiedAt: string | null; expiresAt: string | null; remainingMs: number | null; lockedUntil: string | null; failedAttempts: number };
  escalation: Array<{ id: string; status: string; reason: string | null; requestedBy: string; decidedBy?: string | null }>;
}

const SIGNAL_LABELS: Record<string, string> = {
  ani_match: 'ANI match', cw_contact_phone: 'CW contact phone', known_company_location: 'Known company',
  secure_case_code: 'Ticket code check', sms_otp: 'SMS OTP', email_magic_link: 'Email magic link',
  entra_push: 'Entra push', portal_confirmation: 'Portal confirmation', device_passkey: 'Device passkey',
  manager_approval: 'Manager approval', technician_reverse_code: 'Technician reverse code',
  recent_verified_session: 'Recent verified session', live_agent_approval: 'Dispatcher confirmation',
};

function friendlyError(error: string) {
  if (error.includes('403')) return "Your role doesn't have access to caller verification.";
  if (error.includes('401')) return 'Your session expired — log out and back in.';
  return error + ' — try refreshing.';
}

function outcomeChip(outcome: RuntimeSignalDto['outcome']) {
  const cls = outcome === 'verified'
    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
    : outcome === 'failed'
    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    : outcome === 'pending'
    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
  return <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full font-medium', outcome === 'unavailable' ? '' : 'capitalize', outcome === 'unavailable' && 'font-medium')}>{outcome === 'unavailable' ? 'Unavailable' : outcome}</span>;
}

function dispositionBadge(d: VerificationDto['decision']) {
  const map: Record<string, string> = {
    allow: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    step_up: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    human_approval: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    live_transfer: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    deny_locked: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  };
  return <span className={cn('px-2 py-0.5 rounded-full text-xs font-semibold', map[d.disposition] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300')}>{d.disposition.replace(/_/g, ' ')} · risk {d.risk} · assurance {d.assurance}</span>;
}

/**
 * Caller identity verification panel (issue #49).
 * Suggest-then-confirm: the system proposes identity with confidence + reason;
 * a dispatcher explicitly confirms. Unavailable sources show "Unavailable"
 * with a reason — nothing is fabricated.
 */
export function VerificationPanel({ callId, actorBusy }: { callId: string; actorBusy: boolean }) {
  const { data: v, loading, error, refetch } = useGet<VerificationDto>('/api/v1/calls/' + callId + '/verification');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState<string | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  const [rejectReason, setRejectReason] = React.useState('');
  const [escalateReason, setEscalateReason] = React.useState('');
  const [issuedCode, setIssuedCode] = React.useState<string | null>(null);
  const [readBackCode, setReadBackCode] = React.useState('');
  const [ticketCode, setTicketCode] = React.useState('');
  const [now, setNow] = React.useState(Date.now());
  const disabled = busy || actorBusy;

  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  async function run(fn: () => Promise<string | void>) {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const info = await fn();
      if (info) setMsg(info);
      refetch();
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  if (loading && !v) return <CardSkeleton lines={4} />;
  if (error || !v) {
    return (
      <div className="rounded-xl border border-[hsl(var(--border))] p-6 text-center">
        <p className="font-semibold">Verification unavailable</p>
        <p className="mt-1 text-sm text-[hsl(var(--content-subtle))]">{friendlyError(error ?? 'Not found')}</p>
        <button onClick={refetch} className="mt-3 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white">Try again</button>
      </div>
    );
  }

  const remainingMin = v.session.expiresAt ? Math.max(0, (new Date(v.session.expiresAt).getTime() - now) / 60_000) : null;
  const locked = v.session.lockedUntil && new Date(v.session.lockedUntil).getTime() > now;

  return (
    <section aria-label="Caller identity verification" className="space-y-4">
      {/* Proposed identity */}
      <div className={cn('rounded-xl border p-4', v.proposedIdentity ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/10' : 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/10')}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))]">Proposed identity</p>
            {v.proposedIdentity ? (
              <p className="mt-1 font-bold">
                {v.proposedIdentity.contactName ?? (v.proposedIdentity.contactId != null ? 'Contact #' + v.proposedIdentity.contactId : 'Unknown contact')}
                {v.proposedIdentity.companyName ? ' · ' + v.proposedIdentity.companyName : v.proposedIdentity.companyId != null ? ' · Company #' + v.proposedIdentity.companyId : ''}
                {v.proposedIdentity.ticketId != null ? ' · Ticket #' + v.proposedIdentity.ticketId : ''}
              </p>
            ) : (
              <p className="mt-1 font-bold">Unknown caller</p>
            )}
            <p className="mt-1 text-sm text-[hsl(var(--content-subtle))]">{v.proposalReason}</p>
          </div>
          {v.session.verified && remainingMin != null && (
            <span className="h-fit rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800 dark:bg-green-900/30 dark:text-green-300" data-testid="verify-session-ttl">
              Verified · {Math.ceil(remainingMin)} min left
            </span>
          )}
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {dispositionBadge(v.decision)}
          {!v.session.verified && !locked && (
            <>
              <button
                onClick={() => run(async () => { await apiPost('/api/v1/calls/' + callId + '/verification/confirm', {}); return 'Identity confirmed. Verified session started.'; })}
                disabled={disabled}
                className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50"
              >
                Confirm identity
              </button>
              <div className="flex items-center gap-1">
                <input
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="Mismatch reason (required)"
                  aria-label="Mismatch reason"
                  className="w-44 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-500"
                />
                <button
                  onClick={() => run(async () => {
                    if (!rejectReason.trim()) throw new Error('A mismatch reason is required.');
                    await apiPost('/api/v1/calls/' + callId + '/verification/reject', { reason: rejectReason.trim() });
                    setRejectReason('');
                    return 'Identity rejected and recorded.';
                  })}
                  disabled={disabled}
                  className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 dark:border-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                >
                  Not them
                </button>
              </div>
            </>
          )}
        </div>
        {locked && (
          <p className="mt-2 rounded-lg bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs font-semibold text-red-700 dark:text-red-300">
            Session locked after repeated failures until {new Date(v.session.lockedUntil!).toLocaleTimeString()}.
          </p>
        )}
        {v.decision.requiredMethods.length > 0 && !v.session.verified && (
          <p className="mt-2 text-xs text-[hsl(var(--content-subtle))]">
            Policy requires step-up via: {v.decision.requiredMethods.map(m => SIGNAL_LABELS[m] ?? m).join(', ')}. SMS OTP / Entra push / passkey adapters are not configured yet — use the reverse code, ticket code, or escalate below.
          </p>
        )}
      </div>

      {/* Human-run checks (server-verified) */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))]">Ticket code check</p>
          <p className="mt-1 text-sm text-[hsl(var(--content-subtle))]">Ask the caller for their ticket/case code and enter it — the server compares it against the linked ticket.</p>
          <div className="mt-2 flex items-center gap-2">
            <input
              value={ticketCode}
              onChange={e => setTicketCode(e.target.value)}
              placeholder="Caller's ticket code"
              aria-label="Caller's ticket code"
              className="w-36 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button onClick={() => run(async () => {
              if (!ticketCode.trim()) throw new Error('Enter the ticket code the caller provided.');
              await apiPost('/api/v1/calls/' + callId + '/verification/ticket-code', { code: ticketCode.trim() });
              setTicketCode('');
              return 'Ticket code verified against the linked ticket.';
            })} disabled={disabled} className="rounded-lg bg-brand-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50">Check code</button>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))]">Technician reverse code</p>
          <p className="mt-1 text-sm text-[hsl(var(--content-subtle))]">Issue a code, have a technician send it to the caller, then enter the code the caller reads back — the server verifies it.</p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <button onClick={() => run(async () => {
              const res = await apiPost<{ code: string; expiresAt: string; note: string }>('/api/v1/calls/' + callId + '/verification/reverse-code', {});
              setIssuedCode(res.code);
              setReadBackCode('');
              return undefined;
            })} disabled={disabled} className="rounded-lg bg-brand-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50">Issue code</button>
            <input
              value={readBackCode}
              onChange={e => setReadBackCode(e.target.value)}
              placeholder="Caller's read-back"
              aria-label="Caller's read-back code"
              inputMode="numeric"
              maxLength={6}
              className="w-32 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1.5 text-xs font-mono outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button onClick={() => run(async () => {
              if (!/^\d{6}$/.test(readBackCode.trim())) throw new Error('Enter the 6-digit code the caller read out.');
              await apiPost('/api/v1/calls/' + callId + '/verification/reverse-code/record', { code: readBackCode.trim() });
              setReadBackCode('');
              setIssuedCode(null);
              return 'Read-back verified against the issued code.';
            })} disabled={disabled} className="rounded-lg bg-green-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50">Check read-back</button>
          </div>
          {issuedCode && (
            <div className="mt-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm" data-testid="verify-issued-code">
              <span className="font-mono font-bold tracking-widest">{issuedCode}</span>
              <span className="ml-2 text-xs text-[hsl(var(--content-subtle))]">shown once — relay to a technician (SMS delivery not configured); expires in 5 min, single use</span>
            </div>
          )}
        </div>
      </div>

      {/* Escalation */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))]">Escalation approval</p>
        <p className="mt-1 text-sm text-[hsl(var(--content-subtle))]">When signals are insufficient, escalate to a manager for explicit sign-off.</p>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <input
            value={escalateReason}
            onChange={e => setEscalateReason(e.target.value)}
            placeholder="Escalation reason (required)"
            aria-label="Escalation reason"
            className="w-52 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-500"
          />
          <button
            onClick={() => run(async () => {
              if (!escalateReason.trim()) throw new Error('An escalation reason is required.');
              await apiPost('/api/v1/calls/' + callId + '/verification/escalate', { reason: escalateReason.trim() });
              setEscalateReason('');
              return 'Escalation requested.';
            })}
            disabled={disabled}
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Escalate
          </button>
        </div>
        {v.escalation.length > 0 && (
          <ul className="mt-3 space-y-2">
            {v.escalation.map(a => (
              <li key={a.id} className="flex items-start justify-between gap-3 rounded-lg bg-[hsl(var(--surface-subtle))] px-3 py-2 text-sm">
                <div>
                  <span className={cn('text-xs font-semibold', a.status === 'pending' ? 'text-amber-600 dark:text-amber-300' : a.status === 'approved' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300')}>{a.status}</span>
                  <span className="ml-2 text-[hsl(var(--content-subtle))]">{a.reason}</span>
                  <p className="text-xs text-[hsl(var(--content-placeholder))]">requested by {a.requestedBy}{a.decidedBy ? ' · decided by ' + a.decidedBy : ''}</p>
                </div>
                {a.status === 'pending' && (
                  <div className="flex gap-1.5">
                    <button onClick={() => run(async () => { await apiPost('/api/v1/calls/' + callId + '/verification/escalate/' + a.id + '/decide', { decision: 'approved', reason: 'manager approved' }); return 'Escalation approved.'; })} disabled={disabled} className="rounded-lg bg-green-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50">Approve</button>
                    <button onClick={() => run(async () => { await apiPost('/api/v1/calls/' + callId + '/verification/escalate/' + a.id + '/decide', { decision: 'denied', reason: 'manager denied' }); return 'Escalation denied.'; })} disabled={disabled} className="rounded-lg border border-red-300 px-2 py-1 text-xs font-semibold text-red-700 dark:border-red-700 dark:text-red-300 disabled:opacity-50">Deny</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Signal table */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] overflow-hidden">
        <p className="px-4 pt-3 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))]">Identity signals (13-signal policy)</p>
        <ul className="divide-y divide-[hsl(var(--border))]">
          {v.signals.map(s => (
            <li key={s.method} className="flex items-start gap-3 px-4 py-2.5" data-testid={'verify-signal-' + s.method}>
              <div className="w-44 shrink-0 text-sm font-medium">{SIGNAL_LABELS[s.method] ?? s.method}</div>
              {outcomeChip(s.outcome)}
              <p className="flex-1 text-xs text-[hsl(var(--content-subtle))] leading-relaxed">{s.reason}{s.confidence > 0 ? ' (confidence ' + s.confidence + ')' : ''}</p>
            </li>
          ))}
        </ul>
      </div>

      {msg && <p className="text-xs text-green-700 dark:text-green-300">{msg}</p>}
      {err && <p className="text-xs text-red-600">{err}</p>}
    </section>
  );
}
